/**
 * Quick License Plate Calculator - State Management Hook
 *
 * Central state management for the calculator feature.
 * Handles:
 * - Calculator open/close state
 * - License plate input
 * - RDW lookup orchestration
 * - BPM calculation pipeline
 * - Margin/VAT classification
 * - Max buy price calculation
 * - Keyboard shortcut (⌘L / Ctrl+L)
 *
 * IMPORTANT: This hook is stateless across sessions.
 * No data is persisted to Firestore or localStorage.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { rdwLookup, normalizePlate, type RdwDraft, type RdwResp } from '@/lib/rdwClient';
import { calculateBpm, type VehicleKind } from '@/lib/bpm/forfaitaire';
import { calcGrossBpm, type FuelType } from '@/lib/bpm/gross';
import { classifyVehicle } from './lib/classifyVehicle';
import { calculateMaxBuyPrice } from './lib/calculateMaxBuyPrice';
import {
  type CalculatorState,
  type CalculatorError,
  type BpmResult,
  type RiskFlags,
  type ExistingVehicleResult,
  type UseQuickCalculatorReturn,
  INITIAL_CALCULATOR_STATE,
} from './lib/types';

/**
 * Map RDW error reasons to calculator errors
 */
function mapRdwError(reason: string): CalculatorError {
  switch (reason) {
    case 'INVALID_PLATE':
      return 'INVALID_PLATE';
    case 'NOT_FOUND':
      return 'NOT_FOUND';
    case 'RATE_LIMITED':
      return 'RATE_LIMITED';
    default:
      return 'RDW_UNAVAILABLE';
  }
}

/**
 * Get human-readable error message (Dutch)
 */
function getErrorMessage(error: CalculatorError, retryAfter?: number): string {
  switch (error) {
    case 'INVALID_PLATE':
      return 'Ongeldig kenteken formaat. Gebruik het formaat AB-123-CD of AB123CD.';
    case 'NOT_FOUND':
      return 'Kenteken niet gevonden in RDW. Controleer of het kenteken correct is.';
    case 'RATE_LIMITED':
      if (retryAfter && retryAfter > 0) {
        return `Te veel verzoeken. Probeer over ${retryAfter} seconden opnieuw.`;
      }
      return 'Te veel verzoeken. Probeer het over enkele seconden opnieuw.';
    case 'RDW_UNAVAILABLE':
      return 'RDW-service tijdelijk onbeschikbaar. Probeer het later opnieuw.';
    case 'MISSING_DATA':
      return 'Onvoldoende gegevens beschikbaar om BPM te berekenen.';
  }
}

/**
 * Validate Dutch license plate format
 * Dutch plates are 6 characters (letters and numbers)
 */
function isValidDutchPlate(plate: string): boolean {
  const normalized = normalizePlate(plate);
  // Dutch plates are exactly 6 alphanumeric characters
  return /^[A-Z0-9]{6}$/.test(normalized);
}

/**
 * Map RDW fuel type to BPM fuel type
 */
function mapFuelType(rdwFuelType: string | null | undefined): FuelType {
  if (!rdwFuelType) return 'OVERIG';

  const normalized = rdwFuelType.toLowerCase();

  if (normalized.includes('benzine')) return 'BENZINE';
  if (normalized.includes('diesel')) return 'DIESEL';
  if (normalized.includes('elektr') || normalized === 'electric') return 'ELEKTRISCH';
  if (normalized.includes('hybr')) return 'HYBRIDE';
  if (normalized.includes('lpg')) return 'LPG';

  return 'OVERIG';
}

/**
 * Map RDW vehicle class to BPM vehicle kind
 */
function mapVehicleKind(vehicleClass: string | null | undefined): VehicleKind {
  if (!vehicleClass) return 'PERSONENAUTO';

  const normalized = vehicleClass.toUpperCase();

  if (normalized.includes('BESTEL')) return 'BESTELAUTO';

  return 'PERSONENAUTO';
}

/**
 * Check if fuel type indicates electric vehicle
 */
function isElectricFuel(fuelType: string | null | undefined): boolean {
  if (!fuelType) return false;
  const normalized = fuelType.toLowerCase();
  return normalized.includes('elektr') || normalized === 'electric';
}

/**
 * Calculate BPM from RDW data
 */
function calculateBpmFromRdw(rdwData: RdwDraft): BpmResult {
  const result: BpmResult = {
    grossBpm: null,
    grossBpmSource: 'UNKNOWN',
    restBpm: null,
    depreciationPct: null,
    monthsSinceFirstUse: null,
    bandLabel: null,
    cappedBy5YearRule: false,
  };

  // Electric vehicles have 0 BPM
  if (isElectricFuel(rdwData.fuelType)) {
    result.grossBpm = 0;
    result.grossBpmSource = 'RDW'; // Electric = 0 BPM is a known fact
    result.restBpm = 0;
    result.depreciationPct = 100;
    return result;
  }

  // Get gross BPM - prefer RDW value, fallback to calculation
  let grossBpm: number | null = null;
  let grossBpmSource: BpmResult['grossBpmSource'] = 'UNKNOWN';

  if (rdwData.brutoBpm !== null && rdwData.brutoBpm !== undefined) {
    grossBpm = rdwData.brutoBpm;
    grossBpmSource = 'RDW';
  } else if (rdwData.co2Gkm !== null && rdwData.co2Gkm !== undefined) {
    // Calculate from CO2 if brutoBpm not available
    // NOTE: This uses a placeholder formula, not official tariff tables
    const fuelType = mapFuelType(rdwData.fuelType);
    const tariffYear = rdwData.firstRegDate
      ? new Date(rdwData.firstRegDate).getFullYear()
      : new Date().getFullYear();

    grossBpm = calcGrossBpm({
      co2: rdwData.co2Gkm,
      fuelType,
      tariffYear,
    });
    grossBpmSource = 'CALCULATED';
  }

  result.grossBpm = grossBpm;
  result.grossBpmSource = grossBpmSource;

  // Calculate rest BPM if we have gross BPM and first registration date
  if (grossBpm !== null && grossBpm > 0 && rdwData.firstRegDate) {
    const vehicleKind = mapVehicleKind(rdwData.vehicleClass);
    const today = new Date().toISOString().split('T')[0] as string; // YYYY-MM-DD

    try {
      const bpmCalc = calculateBpm({
        dateFirstUse: rdwData.firstRegDate,
        dateRdwApproval: today,
        vehicleKind,
        grossBpm,
      });

      result.restBpm = bpmCalc.restBpm;
      result.depreciationPct = bpmCalc.depreciationPct;
      result.monthsSinceFirstUse = bpmCalc.monthsTotal;
      result.bandLabel = bpmCalc.band.name;
      result.cappedBy5YearRule = bpmCalc.cappedBy5YearRule;
    } catch {
      // Calculation failed, leave rest BPM as null
    }
  } else if (grossBpm === 0) {
    // Zero gross BPM means zero rest BPM
    result.restBpm = 0;
    result.depreciationPct = 100;
  }

  return result;
}

/**
 * Analyze risk flags from RDW data
 */
function analyzeRiskFlags(rdwData: RdwDraft): RiskFlags {
  const firstRegDate = rdwData.firstRegDate ? new Date(rdwData.firstRegDate) : null;
  const firstRegDateNL = rdwData.firstRegDateNL ? new Date(rdwData.firstRegDateNL) : null;

  return {
    isImport:
      firstRegDate !== null &&
      firstRegDateNL !== null &&
      firstRegDate.getTime() !== firstRegDateNL.getTime(),
    missingCo2: rdwData.co2Gkm === null || rdwData.co2Gkm === undefined,
    missingBpm: rdwData.brutoBpm === null || rdwData.brutoBpm === undefined,
    missingFirstRegDate: !rdwData.firstRegDate,
    isElectric: isElectricFuel(rdwData.fuelType),
  };
}

/**
 * Quick License Plate Calculator Hook (Internal)
 *
 * Provides complete state management for the calculator feature.
 * Registers keyboard shortcut ⌘L / Ctrl+L to toggle the calculator.
 *
 * NOTE: This is the internal implementation. Use useQuickCalculator from
 * QuickCalculatorContext.tsx for shared state across components.
 */
export function useQuickCalculatorState(): UseQuickCalculatorReturn {
  const [state, setState] = useState<CalculatorState>(INITIAL_CALCULATOR_STATE);

  // Track the last calculated plate to prevent duplicate calls
  const lastCalculatedPlateRef = useRef<string | null>(null);

  // Track retry countdown for rate limiting
  const [retryCountdown, setRetryCountdown] = useState<number>(0);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Abort controller for in-flight RDW requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // === KEYBOARD SHORTCUT ===
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Check for ⌘L (Mac) or Ctrl+L (Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? event.metaKey : event.ctrlKey;

      if (modifier && event.key.toLowerCase() === 'l') {
        // Don't override if user is typing in an input (unless it's the calculator's own input)
        const activeElement = document.activeElement;
        const isInputFocused =
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          activeElement?.getAttribute('contenteditable') === 'true';

        // Allow toggle even when calculator input is focused
        const isCalculatorInput = activeElement?.closest('[data-calculator-popover]') !== null;

        if (!isInputFocused || isCalculatorInput) {
          event.preventDefault();
          // Toggle: if open, close; if closed, open
          setState((prev) => {
            if (prev.isOpen) {
              return INITIAL_CALCULATOR_STATE;
            }
            return { ...prev, isOpen: true };
          });
        }
      }

      // Close on Escape (handled by Dialog component, but keep as backup)
      if (event.key === 'Escape' && state.isOpen) {
        setState(INITIAL_CALCULATOR_STATE);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [state.isOpen]);

  // Cleanup retry timer and abort controller on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // === ACTIONS ===

  const open = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const close = useCallback(() => {
    // Abort any in-flight RDW request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setState(INITIAL_CALCULATOR_STATE);
  }, []);

  const reset = useCallback(() => {
    setState(INITIAL_CALCULATOR_STATE);
  }, []);

  const setPlate = useCallback((plate: string) => {
    // Auto-uppercase and allow typing
    setState((prev) => ({
      ...prev,
      plate: plate.toUpperCase(),
      // Clear previous results when plate changes
      rdwData: null,
      bpm: null,
      classification: null,
      riskFlags: null,
      maxBuyPrice: null,
      existingVehicle: null,
      error: null,
      errorMessage: null,
    }));
  }, []);

  const setExistingVehicle = useCallback((result: ExistingVehicleResult) => {
    setState((prev) => ({
      ...prev,
      existingVehicle: result,
    }));
  }, []);

  const setExpectedSellingPrice = useCallback((price: number | null) => {
    setState((prev) => {
      const newState = { ...prev, expectedSellingPrice: price };

      // Recalculate max buy price if we have BPM data
      const restBpm = prev.bpm?.restBpm;
      if (price !== null && restBpm !== null && restBpm !== undefined) {
        newState.maxBuyPrice = calculateMaxBuyPrice(price, prev.targetMarginPct, restBpm);
      } else {
        newState.maxBuyPrice = null;
      }

      return newState;
    });
  }, []);

  const setTargetMarginPct = useCallback((pct: number) => {
    setState((prev) => {
      const newState = { ...prev, targetMarginPct: pct };

      // Recalculate max buy price if we have selling price and BPM
      const restBpm = prev.bpm?.restBpm;
      if (prev.expectedSellingPrice !== null && restBpm !== null && restBpm !== undefined) {
        newState.maxBuyPrice = calculateMaxBuyPrice(prev.expectedSellingPrice, pct, restBpm);
      }

      return newState;
    });
  }, []);

  const calculate = useCallback(async () => {
    const plate = state.plate;
    const normalizedPlate = normalizePlate(plate);

    // Validate plate format
    if (!isValidDutchPlate(plate)) {
      setState((prev) => ({
        ...prev,
        error: 'INVALID_PLATE',
        errorMessage: getErrorMessage('INVALID_PLATE'),
        isLoading: false,
      }));
      return;
    }

    // Prevent duplicate calls for the same plate
    if (normalizedPlate === lastCalculatedPlateRef.current && state.rdwData !== null) {
      // Already have results for this plate, skip
      return;
    }

    // Don't allow retry while countdown is active
    if (retryCountdown > 0) {
      return;
    }

    // Start loading
    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
      errorMessage: null,
    }));

    // Abort any previous in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      // Perform RDW lookup with abort signal
      const response: RdwResp = await rdwLookup(plate, { signal: abortController.signal });

      if (!response.found) {
        // Handle RDW error
        const error = mapRdwError(response.reason);

        // Handle rate limiting with countdown
        let retryAfter: number | undefined;
        if (error === 'RATE_LIMITED' && response.details?.retryAfter) {
          retryAfter = response.details.retryAfter;
          setRetryCountdown(retryAfter);

          // Start countdown timer
          if (retryTimerRef.current) {
            clearInterval(retryTimerRef.current);
          }
          retryTimerRef.current = setInterval(() => {
            setRetryCountdown((prev) => {
              if (prev <= 1) {
                if (retryTimerRef.current) {
                  clearInterval(retryTimerRef.current);
                  retryTimerRef.current = null;
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }

        setState((prev) => ({
          ...prev,
          isLoading: false,
          error,
          errorMessage: getErrorMessage(error, retryAfter),
          // Keep previous results if RDW is unavailable (not rate limited or not found)
          rdwData: error === 'RDW_UNAVAILABLE' ? prev.rdwData : null,
          bpm: error === 'RDW_UNAVAILABLE' ? prev.bpm : null,
          classification: error === 'RDW_UNAVAILABLE' ? prev.classification : null,
          riskFlags: error === 'RDW_UNAVAILABLE' ? prev.riskFlags : null,
          maxBuyPrice: error === 'RDW_UNAVAILABLE' ? prev.maxBuyPrice : null,
        }));
        return;
      }

      // Extract RDW data
      const rdwData = response.merged || response.draft;

      if (!rdwData) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: 'MISSING_DATA',
          errorMessage: getErrorMessage('MISSING_DATA'),
        }));
        return;
      }

      // Calculate BPM
      const bpm = calculateBpmFromRdw(rdwData);

      // Classify vehicle
      const classification = classifyVehicle(rdwData);

      // Analyze risk flags
      const riskFlags = analyzeRiskFlags(rdwData);

      // Calculate max buy price if we have selling price
      let maxBuyPrice: number | null = null;
      if (state.expectedSellingPrice !== null && bpm.restBpm !== null) {
        maxBuyPrice = calculateMaxBuyPrice(
          state.expectedSellingPrice,
          state.targetMarginPct,
          bpm.restBpm
        );
      }

      // Track successful calculation
      lastCalculatedPlateRef.current = normalizedPlate;

      // Update state with results
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: null,
        errorMessage: null,
        rdwData,
        bpm,
        classification,
        riskFlags,
        maxBuyPrice,
      }));
    } catch (error) {
      // Ignore aborted requests (user closed calculator or started new request)
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      // Network or unexpected error - keep previous results if available
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: 'RDW_UNAVAILABLE',
        errorMessage: getErrorMessage('RDW_UNAVAILABLE'),
        // Keep previous results visible with warning
        rdwData: prev.rdwData,
        bpm: prev.bpm,
        classification: prev.classification,
        riskFlags: prev.riskFlags,
        maxBuyPrice: prev.maxBuyPrice,
      }));
    }
  }, [
    state.plate,
    state.expectedSellingPrice,
    state.targetMarginPct,
    state.rdwData,
    retryCountdown,
  ]);

  return {
    state,
    retryCountdown,
    open,
    close,
    setPlate,
    setExpectedSellingPrice,
    setTargetMarginPct,
    calculate,
    reset,
    setExistingVehicle,
  };
}
